import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
} from 'recharts';
import { FiChevronLeft, FiChevronRight, FiFilter, FiRefreshCw } from 'react-icons/fi';
import Layout from '../components/Layout';
import AnalyticsDateRange from '../components/AnalyticsDateRange';
import { formatSingaporeDateTime } from '../utils/singaporeTime';
import { getErpWebAssetUrl } from '../utils/erpWebAssets';
import { useAnalyticsChartRange, clipAnalyticsChartData, ANALYTICS_DATE_XAXIS_PROPS } from '../hooks/useAnalyticsChartRange';

const PAGE_SIZE = 10;
const LINE_COLORS = [
    '#2563eb',
    '#16a34a',
    '#dc2626',
    '#7c3aed',
    '#d97706',
    '#0891b2',
    '#db2777',
    '#4f46e5',
    '#65a30d',
    '#ea580c',
];

const formatDeviceDisplay = (device) => {
    const d = (device || '').trim().toLowerCase();
    if (!d) return '—';
    if (d.includes('mobile') || d.includes('phone') || d.includes('tablet')) return '📲';
    if (d.includes('desktop')) return '💻';
    return device;
};

const StatCard = ({ label, visitors, views }) => (
    <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <div className="mt-2 flex gap-6">
            <div>
                <p className="text-2xl font-bold text-blue-700">{visitors ?? 0}</p>
                <p className="text-xs text-gray-500">Visitors</p>
            </div>
            <div>
                <p className="text-2xl font-bold text-green-700">{views ?? 0}</p>
                <p className="text-xs text-gray-500">Views</p>
            </div>
        </div>
    </div>
);

const TablePagination = ({ page, totalPages, onPageChange }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-gray-50 text-sm">
            <span className="text-gray-500">
                Page {page} of {totalPages}
            </span>
            <div className="flex gap-1">
                <button
                    type="button"
                    onClick={() => onPageChange(page - 1)}
                    disabled={page === 1}
                    className={`p-1 rounded ${page === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Previous page"
                >
                    <FiChevronLeft />
                </button>
                <button
                    type="button"
                    onClick={() => onPageChange(page + 1)}
                    disabled={page === totalPages}
                    className={`p-1 rounded ${page === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-600 hover:bg-gray-200'}`}
                    aria-label="Next page"
                >
                    <FiChevronRight />
                </button>
            </div>
        </div>
    );
};

const PaginatedDataTable = ({ title, columns, rows, emptyText = 'No data' }) => {
    const [page, setPage] = useState(1);
    const allRows = rows || [];
    const totalPages = Math.max(1, Math.ceil(allRows.length / PAGE_SIZE));

    useEffect(() => {
        setPage(1);
    }, [rows]);

    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
    }, [page, totalPages]);

    const paginatedRows = allRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    return (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col h-full">
            <div className="px-4 py-3 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-800">{title}</h2>
            </div>
            <div className="overflow-x-auto flex-1">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase whitespace-nowrap"
                                >
                                    {col.label}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                        {!paginatedRows.length ? (
                            <tr>
                                <td colSpan={columns.length} className="px-3 py-6 text-center text-gray-500 text-sm">
                                    {emptyText}
                                </td>
                            </tr>
                        ) : (
                            paginatedRows.map((row, i) => (
                                <tr key={row._id || row.visitorId || row.path || `${page}-${i}`} className="hover:bg-gray-50">
                                    {columns.map((col) => (
                                        <td key={col.key} className="px-3 py-2 text-sm text-gray-800">
                                            {col.render ? col.render(row) : row[col.key] ?? '—'}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <TablePagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
    );
};

function analyticsName(row) {
    const name = String(row?.name || '').trim();
    return name || String(row?.domain || '').trim();
}

function FilterCheckboxPanel({
    buttonLabel,
    buttonClassName,
    open,
    onToggle,
    panelRef,
    items,
    isChecked,
    onToggleItem,
    onSelectAll,
    onUnselectAll,
    onSave,
    emptyText,
}) {
    const allChecked = items.length > 0 && items.every((item) => isChecked(item));
    return (
        <div className="relative" ref={panelRef}>
            <button
                type="button"
                onClick={onToggle}
                className={`inline-flex items-center gap-2 border rounded px-3 py-1 text-sm ${buttonClassName}`}
            >
                <FiFilter className="w-4 h-4" />
                {buttonLabel}
            </button>
            {open && (
                <div className="absolute left-0 z-20 mt-1 w-[min(92vw,56rem)] rounded-md border border-gray-200 bg-white shadow-lg">
                    {items.length > 0 && (
                        <div className="flex justify-end px-3 pt-2">
                            <button
                                type="button"
                                onClick={allChecked ? onUnselectAll : onSelectAll}
                                className="text-sm text-blue-600 hover:text-blue-800"
                            >
                                {allChecked ? 'Unselect All' : 'Select All'}
                            </button>
                        </div>
                    )}
                    <div className="max-h-72 overflow-y-auto p-3 pt-2">
                        {items.length === 0 ? (
                            <p className="text-sm text-gray-500">{emptyText}</p>
                        ) : (
                            <div className="grid grid-cols-5 gap-x-3 gap-y-1">
                                {items.map((item) => (
                                    <label
                                        key={item.id}
                                        className="flex items-center gap-1.5 text-sm text-gray-700 min-w-0 px-0.5 py-0.5 rounded hover:bg-gray-50"
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isChecked(item)}
                                            onChange={() => onToggleItem(item)}
                                        />
                                        <span className="truncate" title={item.label}>
                                            {item.label}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        )}
                    </div>
                    <div className="border-t border-gray-200 p-2">
                        <button
                            type="button"
                            onClick={onSave}
                            className="w-full rounded-md bg-blue-600 text-white text-sm py-1.5 hover:bg-blue-700"
                        >
                            Save
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

const RedirectAnalyticsPage = () => {
    const [dashboard, setDashboard] = useState(null);
    const [chartData, setChartData] = useState([]);
    const [chartLines, setChartLines] = useState([]);
    const [groups, setGroups] = useState([]);
    const [domains, setDomains] = useState([]);
    const [groupFilterOpen, setGroupFilterOpen] = useState(false);
    const [domainFilterOpen, setDomainFilterOpen] = useState(false);
    const [appliedGroupIds, setAppliedGroupIds] = useState(null);
    const [draftGroupIds, setDraftGroupIds] = useState(new Set());
    const [appliedNames, setAppliedNames] = useState(null);
    const [draftNames, setDraftNames] = useState(new Set());
    const groupFilterRef = useRef(null);
    const domainFilterRef = useRef(null);
    const { chartFrom, setChartFrom, chartTo, setChartTo, rangeReady } = useAnalyticsChartRange();
    const [loading, setLoading] = useState(true);
    const [chartLoading, setChartLoading] = useState(true);
    const [error, setError] = useState('');

    const authHeaders = () => ({
        Authorization: `Bearer ${localStorage.getItem('token')}`,
        'ngrok-skip-browser-warning': 'true',
    });

    const allGroupIds = useMemo(() => groups.map((g) => String(g._id)), [groups]);

    const availableDomains = useMemo(() => {
        if (appliedGroupIds === null) return domains;
        const idSet = new Set(appliedGroupIds);
        return domains.filter((row) =>
            (row.groups || []).some((g) => idSet.has(String(g._id || g)))
        );
    }, [domains, appliedGroupIds]);

    const availableNameItems = useMemo(() => {
        const seen = new Set();
        const items = [];
        for (const row of availableDomains) {
            const label = analyticsName(row);
            if (!label || seen.has(label)) continue;
            seen.add(label);
            items.push({ id: label, label });
        }
        return items.sort((a, b) => a.label.localeCompare(b.label));
    }, [availableDomains]);

    const availableNameSet = useMemo(
        () => new Set(availableNameItems.map((item) => item.id)),
        [availableNameItems]
    );

    const selectedHosts = useMemo(() => {
        return availableDomains
            .filter((row) => {
                if (appliedNames === null) return true;
                return appliedNames.includes(analyticsName(row));
            })
            .map((row) => String(row.domain || '').toLowerCase())
            .filter(Boolean);
    }, [availableDomains, appliedNames]);

    const fetchFilterOptions = useCallback(async () => {
        try {
            const [groupRes, domainRes] = await Promise.all([
                axios.get(
                    `${process.env.REACT_APP_API_URL}/api/redirect-groups?limit=200&sort=groupName`,
                    { headers: authHeaders() }
                ),
                axios.get(
                    `${process.env.REACT_APP_API_URL}/api/redirect-domains?limit=200&sort=name`,
                    { headers: authHeaders() }
                ),
            ]);
            if (groupRes.data.success) setGroups(groupRes.data.data || []);
            if (domainRes.data.success) setDomains(domainRes.data.data || []);
        } catch {
            // filters stay empty
        }
    }, []);

    const fetchDashboard = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const params = {};
            if (appliedGroupIds !== null) {
                params.groupIds = appliedGroupIds.length ? appliedGroupIds.join(',') : 'none';
            }
            if (appliedNames !== null) {
                params.names = appliedNames.length ? appliedNames.join(',') : 'none';
            }
            if (chartFrom) params.from = chartFrom;
            if (chartTo) params.to = chartTo;
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/redirect-analytics/dashboard`,
                { headers: authHeaders(), params }
            );
            if (res.data.success) setDashboard(res.data.data);
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load analytics');
        } finally {
            setLoading(false);
        }
    }, [appliedGroupIds, appliedNames, chartFrom, chartTo]);

    const fetchChart = useCallback(async () => {
        if (!chartFrom || !chartTo) return;
        try {
            setChartLoading(true);
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/redirect-analytics/chart`,
                {
                    headers: authHeaders(),
                    params: { from: chartFrom, to: chartTo },
                }
            );
            if (res.data.success) {
                setChartData(res.data.data?.series || []);
                setChartLines(res.data.data?.lines || []);
            }
        } catch (err) {
            setError(err.response?.data?.error || err.message || 'Failed to load chart');
        } finally {
            setChartLoading(false);
        }
    }, [chartFrom, chartTo]);

    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    useEffect(() => {
        if (!rangeReady) return;
        fetchChart();
    }, [rangeReady, fetchChart]);

    useEffect(() => {
        if (!groupFilterOpen && !domainFilterOpen) return undefined;
        const onPointerDown = (event) => {
            if (groupFilterRef.current && !groupFilterRef.current.contains(event.target)) {
                setGroupFilterOpen(false);
            }
            if (domainFilterRef.current && !domainFilterRef.current.contains(event.target)) {
                setDomainFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, [groupFilterOpen, domainFilterOpen]);

    const openGroupFilter = () => {
        setDraftGroupIds(new Set(appliedGroupIds === null ? allGroupIds : appliedGroupIds));
        setDomainFilterOpen(false);
        setGroupFilterOpen((open) => !open);
    };

    const openDomainFilter = () => {
        const current = appliedNames === null
            ? availableNameItems.map((item) => item.id)
            : appliedNames.filter((name) => availableNameSet.has(name));
        setDraftNames(new Set(current));
        setGroupFilterOpen(false);
        setDomainFilterOpen((open) => !open);
    };

    const toggleDraftGroup = (id) => {
        setDraftGroupIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleDraftName = (name) => {
        setDraftNames((prev) => {
            const next = new Set(prev);
            if (next.has(name)) next.delete(name);
            else next.add(name);
            return next;
        });
    };

    const saveGroupFilter = () => {
        const selected = allGroupIds.filter((id) => draftGroupIds.has(id));
        setAppliedGroupIds(selected.length === allGroupIds.length ? null : selected);
        setAppliedNames(null);
        setGroupFilterOpen(false);
    };

    const saveDomainFilter = () => {
        const selected = availableNameItems.map((item) => item.id).filter((id) => draftNames.has(id));
        setAppliedNames(selected.length === availableNameItems.length ? null : selected);
        setDomainFilterOpen(false);
    };

    const visibleLines = useMemo(() => {
        if (appliedGroupIds === null && appliedNames === null) return chartLines;
        const hostSet = new Set(selectedHosts);
        return chartLines.filter((line) => hostSet.has(String(line.domain || '').toLowerCase()));
    }, [chartLines, appliedGroupIds, appliedNames, selectedHosts]);
    const summary = dashboard?.trafficSummary;
    const trafficSeries = clipAnalyticsChartData(chartData, chartFrom, chartTo);

    const activeVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'views', label: 'Views' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'lastViewedAt',
            label: 'Last seen',
            render: (row) => formatSingaporeDateTime(row.lastViewedAt),
        },
    ];

    const latestVisitorColumns = [
        { key: 'path', label: 'Page' },
        { key: 'ip', label: 'IP' },
        { key: 'browser', label: 'Browser' },
        { key: 'device', label: 'Device', render: (row) => formatDeviceDisplay(row.device) },
        { key: 'country', label: 'Country' },
        {
            key: 'viewedAt',
            label: 'Viewed at',
            render: (row) => formatSingaporeDateTime(row.viewedAt),
        },
    ];

    return (
        <Layout>
            <div className="px-2 w-full max-w-none p-6 space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <img
                            src={getErpWebAssetUrl('redirectlogo.png')}
                            alt=""
                            className="h-8 w-8 object-contain"
                        />
                        <h1 className="text-2xl font-bold">Redirect Analytics</h1>
                        <FilterCheckboxPanel
                            buttonLabel="Filter Groups"
                            buttonClassName="bg-violet-100 border-violet-200 hover:bg-violet-200"
                            open={groupFilterOpen}
                            onToggle={openGroupFilter}
                            panelRef={groupFilterRef}
                            items={groups.map((g) => ({
                                id: String(g._id),
                                label: g.groupName,
                            }))}
                            isChecked={(item) => draftGroupIds.has(item.id)}
                            onToggleItem={(item) => toggleDraftGroup(item.id)}
                            onSelectAll={() => setDraftGroupIds(new Set(allGroupIds))}
                            onUnselectAll={() => setDraftGroupIds(new Set())}
                            onSave={saveGroupFilter}
                            emptyText="No groups"
                        />
                        <FilterCheckboxPanel
                            buttonLabel="Filter Domains"
                            buttonClassName="bg-sky-100 border-sky-200 hover:bg-sky-200"
                            open={domainFilterOpen}
                            onToggle={openDomainFilter}
                            panelRef={domainFilterRef}
                            items={availableNameItems}
                            isChecked={(item) => draftNames.has(item.id)}
                            onToggleItem={(item) => toggleDraftName(item.id)}
                            onSelectAll={() =>
                                setDraftNames(new Set(availableNameItems.map((item) => item.id)))
                            }
                            onUnselectAll={() => setDraftNames(new Set())}
                            onSave={saveDomainFilter}
                            emptyText="No domain names"
                        />
                    </div>
                    <div className="flex flex-wrap items-end gap-3">
                        <AnalyticsDateRange
                            chartFrom={chartFrom}
                            setChartFrom={setChartFrom}
                            chartTo={chartTo}
                            setChartTo={setChartTo}
                        />
                        <button
                            type="button"
                            onClick={() => {
                                fetchDashboard();
                                fetchChart();
                            }}
                            className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
                        >
                            <FiRefreshCw className="w-4 h-4" />
                            Refresh
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="p-4 bg-red-100 text-red-700 rounded-md">{error}</div>
                )}

                <div className="bg-white rounded-lg border border-gray-200 p-4 shadow-sm">
                    <div className="mb-4">
                        <h2 className="text-lg font-semibold text-gray-800">
                            Views over time
                        </h2>
                    </div>
                    {chartLoading ? (
                        <p className="text-gray-500 py-12 text-center">Loading chart...</p>
                    ) : trafficSeries.length === 0 || chartLines.length === 0 ? (
                        <p className="text-gray-500 py-12 text-center">No data for selected range</p>
                    ) : visibleLines.length === 0 ? (
                        <p className="text-gray-500 py-12 text-center">No domains selected</p>
                    ) : (
                        <ResponsiveContainer width="100%" height={320}>
                            <LineChart key={`traffic-${chartFrom}-${chartTo}-${selectedHosts.join(',')}`} data={trafficSeries}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis {...ANALYTICS_DATE_XAXIS_PROPS} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                                <Tooltip />
                                <Legend />
                                {visibleLines.map((line) => {
                                    const colorIndex = chartLines.findIndex((row) => row.key === line.key);
                                    return (
                                    <Line
                                        key={line.key}
                                        type="monotone"
                                        dataKey={line.key}
                                        name={line.label}
                                        stroke={LINE_COLORS[(colorIndex < 0 ? 0 : colorIndex) % LINE_COLORS.length]}
                                        strokeWidth={2}
                                        dot={false}
                                    />
                                    );
                                })}
                            </LineChart>
                        </ResponsiveContainer>
                    )}
                </div>

                {loading ? (
                    <p className="text-gray-600">Loading dashboard...</p>
                ) : dashboard ? (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                            <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
                                <p className="text-sm font-medium text-amber-800">Currently online</p>
                                <p className="text-3xl font-bold text-amber-700 mt-1">
                                    {dashboard.onlineNow ?? 0}
                                </p>
                                <p className="text-xs text-amber-600 mt-1">Last 15 minutes</p>
                            </div>
                            <StatCard label="Today" {...summary?.today} />
                            <StatCard label="Yesterday" {...summary?.yesterday} />
                            <StatCard label="Last 7 days" {...summary?.last7Days} />
                            <StatCard label="Last 30 days" {...summary?.last30Days} />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
                            <PaginatedDataTable
                                title="Referrals from search engines"
                                columns={[
                                    { key: 'searchEngine', label: 'Search engine' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.searchReferrals}
                            />
                            <PaginatedDataTable
                                title="Top pages"
                                columns={[
                                    { key: 'path', label: 'Page' },
                                    { key: 'views', label: 'Views' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topPages}
                            />
                            <PaginatedDataTable
                                title="Traffic sources — UTM"
                                columns={[
                                    { key: 'utmSource', label: 'Source' },
                                    { key: 'utmMedium', label: 'Medium' },
                                    { key: 'utmCampaign', label: 'Campaign' },
                                    { key: 'visits', label: 'Visits' },
                                ]}
                                rows={dashboard.utmSources}
                            />
                            <PaginatedDataTable
                                title="Top referring sites"
                                columns={[
                                    { key: 'referrerDomain', label: 'Site' },
                                    { key: 'visits', label: 'Visits' },
                                    { key: 'uniqueVisitors', label: 'Visitors' },
                                ]}
                                rows={dashboard.topReferrers}
                            />
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <PaginatedDataTable
                                title="Active visitors"
                                columns={activeVisitorColumns}
                                rows={dashboard.activeVisitors}
                            />
                            <PaginatedDataTable
                                title="Latest visitors"
                                columns={latestVisitorColumns}
                                rows={dashboard.latestVisitors}
                            />
                        </div>
                    </>
                ) : null}
            </div>
        </Layout>
    );
};

export default RedirectAnalyticsPage;
